/**
 * Engine selection rules (M3 contract 1), mirrored from the Python source of truth
 * (`app/analysis/shared/domain/engines.py`) and the Node catalogue (`engineCatalog.ts`).
 *
 * Everything here is pure: the EngineSelector feeds the catalogue returned by
 * `GET /videos/engines` and the user's selection through `applySelection`, and the launch
 * flows use `buildRunOptions` to shape the camelCase `runOptions` body (contract 8).
 */

export const ENGINE_NAMES = Object.freeze([
  'gestures',
  'coherence',
  'cultural_fit',
  'client_questions',
  'pre_competency',
  'toolkit',
  'cv',
]);

export const SOURCE_TYPES = Object.freeze(['interview', 'manual_analysis', 'cv']);

/**
 * Static copy of the catalogue served by `GET /videos/engines`. Used as a fallback when the
 * endpoint is unavailable so the selector still renders the agreed rules.
 */
export const DEFAULT_ENGINE_CATALOGUE = Object.freeze([
  {
    name: 'gestures',
    label: 'Gestures',
    description: 'Facial and body gesture detection over the video.',
    requires: [],
    implied_by: ['coherence'],
    degraded_without: [],
    sources: ['interview', 'manual_analysis'],
  },
  {
    name: 'coherence',
    label: 'Coherence',
    description: 'Transcription, idea segmentation and gesture-contradiction scoring.',
    requires: ['gestures'],
    implied_by: [],
    degraded_without: [],
    sources: ['interview', 'manual_analysis'],
  },
  {
    name: 'cultural_fit',
    label: 'Cultural fit',
    description: 'Cultural fit inference over the interview conversation.',
    requires: [],
    implied_by: [],
    degraded_without: [],
    sources: ['interview'],
  },
  {
    name: 'client_questions',
    label: 'Client questions',
    description: 'Answers to the client questions extracted from the conversation.',
    requires: [],
    implied_by: [],
    degraded_without: [],
    sources: ['interview'],
  },
  {
    name: 'pre_competency',
    label: 'Pre-competency',
    description: 'Pre-competency assessment over the interview conversation.',
    requires: [],
    implied_by: [],
    degraded_without: [],
    sources: ['interview'],
  },
  {
    name: 'toolkit',
    label: 'Toolkit (Triple A)',
    description: 'Triple A toolkit; runs degraded without the pre-competency output.',
    requires: [],
    implied_by: [],
    degraded_without: ['pre_competency'],
    sources: ['interview'],
  },
  {
    name: 'cv',
    label: 'CV',
    description: 'CV analysis.',
    requires: [],
    implied_by: [],
    degraded_without: [],
    sources: ['cv'],
  },
]);

const asArray = (value) => (Array.isArray(value) ? value : []);

/**
 * Normalizes any catalogue-like payload (API or fallback) into entries with every array field
 * present, dropping entries without a name.
 * @param {Array<object>|null|undefined} catalogue
 * @returns {Array<{name: string, label: string, description: string, requires: string[],
 *   implied_by: string[], degraded_without: string[], sources: string[]}>}
 */
export function normalizeCatalogue(catalogue) {
  return asArray(catalogue)
    .filter((entry) => entry && typeof entry.name === 'string' && entry.name.trim())
    .map((entry) => ({
      name: entry.name,
      label: typeof entry.label === 'string' && entry.label ? entry.label : entry.name,
      description: typeof entry.description === 'string' ? entry.description : '',
      requires: asArray(entry.requires),
      implied_by: asArray(entry.implied_by ?? entry.impliedBy),
      degraded_without: asArray(entry.degraded_without ?? entry.degradedWithout),
      sources: asArray(entry.sources),
    }));
}

/**
 * Catalogue entries selectable for a source type, in catalogue order.
 * @param {Array<object>} catalogue
 * @param {string} sourceType
 */
export function selectableEngines(catalogue, sourceType) {
  return normalizeCatalogue(catalogue).filter((entry) => entry.sources.includes(sourceType));
}

/**
 * Default selection for a source: every selectable engine (matches Python's "absent
 * run_options -> all selectable engines").
 * @param {Array<object>} catalogue
 * @param {string} sourceType
 * @returns {string[]}
 */
export function defaultSelection(catalogue, sourceType) {
  return selectableEngines(catalogue, sourceType).map((entry) => entry.name);
}

/**
 * Human label of an engine name from a catalogue (falls back to the raw name).
 * @param {Array<object>} catalogue
 * @param {string} name
 * @returns {string}
 */
export function engineLabel(catalogue, name) {
  const entry = normalizeCatalogue(catalogue).find((item) => item.name === name);
  return entry ? entry.label : String(name);
}

/**
 * Applies the dependency rules to a user selection.
 *
 * - `requires` closes the selection transitively; engines added this way are reported in
 *   `auto_added` (e.g. coherence -> gestures, "required by Coherence").
 * - `degraded_without` produces a warning when the dependency is not effective
 *   (e.g. toolkit without pre_competency, "will run degraded").
 * - Unknown names or engines not selectable for the source invalidate the selection.
 * - An empty effective selection is invalid (`error: 'engines_empty'`).
 *
 * @param {Array<object>} catalogue catalogue entries (API shape or DEFAULT_ENGINE_CATALOGUE)
 * @param {string} sourceType 'interview' | 'manual_analysis' | 'cv'
 * @param {string[]} selected engines explicitly requested by the user
 * @returns {{ engines: string[], requested: string[], auto_added: string[],
 *   warnings: Array<{engine: string, code: string, related: string[], message: string}>,
 *   valid: boolean, error: string|null }}
 */
export function applySelection(catalogue, sourceType, selected) {
  const selectable = selectableEngines(catalogue, sourceType);
  const selectableByName = new Map(selectable.map((entry) => [entry.name, entry]));
  const allNames = new Set(normalizeCatalogue(catalogue).map((entry) => entry.name));
  const warnings = [];

  const requested = [];
  asArray(selected).forEach((name) => {
    if (typeof name !== 'string' || requested.includes(name)) return;
    if (!allNames.has(name)) {
      warnings.push({
        engine: name,
        code: 'engine_unknown',
        related: [],
        message: `Unknown engine "${name}"`,
      });
      return;
    }
    if (!selectableByName.has(name)) {
      warnings.push({
        engine: name,
        code: 'engine_not_selectable',
        related: [],
        message: `${engineLabel(catalogue, name)} is not available for this source`,
      });
      return;
    }
    requested.push(name);
  });

  // Transitive closure over `requires`, remembering which engine pulled each dependency in.
  const effective = new Set(requested);
  const addedBy = new Map();
  const queue = [...requested];
  while (queue.length) {
    const name = queue.shift();
    const entry = selectableByName.get(name);
    if (!entry) continue;
    entry.requires.forEach((dependency) => {
      if (!selectableByName.has(dependency) || effective.has(dependency)) return;
      effective.add(dependency);
      addedBy.set(dependency, name);
      queue.push(dependency);
    });
  }

  const engines = selectable.map((entry) => entry.name).filter((name) => effective.has(name));
  const autoAdded = engines.filter((name) => addedBy.has(name));

  autoAdded.forEach((name) => {
    const by = addedBy.get(name);
    warnings.push({
      engine: name,
      code: 'required_by',
      related: [by],
      message: `required by ${engineLabel(catalogue, by)}`,
    });
  });

  engines.forEach((name) => {
    const entry = selectableByName.get(name);
    const missing = entry.degraded_without.filter((dependency) => !effective.has(dependency));
    if (missing.length) {
      warnings.push({
        engine: name,
        code: 'degraded_without',
        related: missing,
        message: `will run degraded without ${missing
          .map((dependency) => engineLabel(catalogue, dependency))
          .join(', ')}`,
      });
    }
  });

  const hasBlockingWarning = warnings.some(
    (warning) => warning.code === 'engine_unknown' || warning.code === 'engine_not_selectable'
  );
  let error = null;
  if (engines.length === 0) error = 'engines_empty';
  else if (hasBlockingWarning)
    error = warnings.find((warning) => warning.code !== 'required_by').code;

  return {
    engines,
    requested,
    auto_added: autoAdded,
    warnings,
    valid: error === null,
    error,
  };
}

/**
 * Engines whose switch must stay locked on because a selected engine requires them.
 * @returns {Record<string, string>} engine -> engine that requires it
 */
export function lockedEngines(catalogue, sourceType, selected) {
  const result = applySelection(catalogue, sourceType, selected);
  const locked = {};
  result.warnings
    .filter((warning) => warning.code === 'required_by')
    .forEach((warning) => {
      locked[warning.engine] = warning.related[0];
    });
  return locked;
}

const cleanText = (value) => (typeof value === 'string' && value.trim() ? value.trim() : null);

/**
 * Builds the camelCase `runOptions` HTTP body of contract 8 from the dialog state. Only the
 * fields that carry a value are included; `engines` is always present.
 *
 * @param {{ engines: string[], configVersionId?: string|null, batch?: string|null,
 *   note?: string|null, promoteOnSuccess?: boolean|null }} options
 * @returns {{ engines: string[], configVersionId?: string, batch?: string, note?: string,
 *   promoteOnSuccess?: boolean }}
 */
export function buildRunOptions(options = {}) {
  const body = { engines: asArray(options.engines).filter((name) => typeof name === 'string') };
  const configVersionId = cleanText(options.configVersionId);
  if (configVersionId) body.configVersionId = configVersionId;
  const batch = cleanText(options.batch);
  if (batch) body.batch = batch;
  const note = cleanText(options.note);
  if (note) body.note = note;
  if (typeof options.promoteOnSuccess === 'boolean')
    body.promoteOnSuccess = options.promoteOnSuccess;
  return body;
}

/**
 * Summary line shown at the end of the launch dialog, e.g.
 * `gestures 2.0.3 + coherence 1.4.2 · Config Base v7 · 3 videos`.
 *
 * @param {{ engines: string[], versions?: Record<string, string>|null, configName?: string|null,
 *   configVersion?: number|string|null, targetCount?: number, targetNoun?: string }} params
 * @returns {string}
 */
export function formatLaunchSummary({
  engines,
  versions = null,
  configName = null,
  configVersion = null,
  targetCount = 0,
  targetNoun = 'video',
}) {
  const parts = [];
  const enginePart = asArray(engines)
    .map((name) => {
      const version = versions && versions[name] ? ` ${versions[name]}` : '';
      return `${name}${version}`;
    })
    .join(' + ');
  if (enginePart) parts.push(enginePart);
  if (configName) {
    const versionSuffix =
      configVersion !== null && configVersion !== undefined && configVersion !== ''
        ? ` v${configVersion}`
        : '';
    parts.push(`Config ${configName}${versionSuffix}`);
  }
  if (targetCount > 0) {
    parts.push(`${targetCount} ${targetNoun}${targetCount === 1 ? '' : 's'}`);
  }
  return parts.join(' · ');
}
